import type { SupportedCurrency } from '@editor/types/SubscriptionPrice';
import { formatMonetaryValue } from '@editor/utils/billing';
import type {
  Subscription,
  SubscriptionChangePreview,
} from '@repo/subscription/api';
import { addDays } from 'date-fns';

import { FREE_TRIAL_DAYS_AMOUNT } from '../../../global/constants';
import useFreeTrial, { FreeTrialData } from '../../../hooks/useFreeTrial';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../../stores/planSelectionStore';
import { calculateDiscount, getDateLabel, isSameDate } from '../utils';

import { NextChargeData, NextChargeParams } from './types';
import usePlanPrice from './usePlanPrice';
import useSubscriptionChangeState from './useSubscriptionChangeState';

const getNextChargeDateAndLabel = (
  currentSubscription: Subscription,
  changeSubscriptionPreview: SubscriptionChangePreview | null | undefined,
  freeTrial: FreeTrialData,
  planChangeState: ReturnType<typeof useSubscriptionChangeState>
): { date?: Date; label: string } => {
  if (!planChangeState && currentSubscription.pastDue) {
    return {
      label: `Payment due today`,
    };
  }

  if (!planChangeState && freeTrial.isCancelled && freeTrial.isRunning) {
    return {
      label: `Free trial canceled due ${getDateLabel(freeTrial.trialEndDate)}`,
    };
  }

  if (!planChangeState && currentSubscription.canceledAt) {
    return {
      label: `Subscription canceled due ${getDateLabel(
        currentSubscription.currentPeriodEnd
      )}`,
    };
  }

  let date: string | Date | undefined | null =
    changeSubscriptionPreview?.nextInvoiceDate ??
    currentSubscription.nextInvoiceDate ??
    currentSubscription.currentPeriodEnd;

  let prefix = 'Payment';

  if (freeTrial.isRunning && planChangeState?.planChanged) {
    date = new Date();
  } else if (freeTrial.isAvailable) {
    date = addDays(new Date(), FREE_TRIAL_DAYS_AMOUNT);
  } else if (freeTrial.isRunning && !freeTrial.isCancelled) {
    date = freeTrial.trialEndDate;
    prefix = 'Free trial';
  }

  const dateLabel =
    !date || isSameDate(new Date(date), new Date())
      ? 'today'
      : getDateLabel(date);

  return {
    label: `${prefix} due ${dateLabel}`,
    date: date ? new Date(date) : undefined,
  };
};

/**
 * Get the seats data for the next charge
 * @param changeSubscriptionPreview - The change subscription preview
 * @param currency - The currency
 * @returns The seats data
 */
const getSeatsData = (
  changeSubscriptionPreview: SubscriptionChangePreview | null | undefined,
  currency: SupportedCurrency
): NextChargeData['seats'] | undefined => {
  const seats = changeSubscriptionPreview?.activeSeats;
  const purchaseSeats = changeSubscriptionPreview?.plan.quantity;
  if (!seats || !purchaseSeats) {
    return undefined;
  }

  const pricePerMonth =
    changeSubscriptionPreview.plan.amount /
    (changeSubscriptionPreview.plan.interval === 'year' ? 12 : 1);
  const price = formatMonetaryValue({
    amount: pricePerMonth,
    currency,
  });

  const totalAmount = changeSubscriptionPreview.plan.amount * purchaseSeats;

  return {
    amount: purchaseSeats,
    total: formatMonetaryValue({
      amount: totalAmount,
      currency,
    }),
    clarification: `${price}/mo × ${purchaseSeats} editors × ${
      changeSubscriptionPreview.plan.interval === 'year'
        ? '12 months'
        : '1 month'
    }`,
  };
};

const FREE_TRIAL_PAYMENT_NOTE = {
  title: 'Total due today',
  description: `(${FREE_TRIAL_DAYS_AMOUNT}-day free trial)`,
  amount: 0,
};

/**
 * Custom hook to calculate the next charge details for a subscription.
 *
 * This hook calculates the next charge details based on the current subscription, selected plan, billing interval, quantity, promo code etc.
 */
const useNextCharge = ({
  changeSubscriptionPreview,
  currentSubscription,
}: NextChargeParams): NextChargeData | null => {
  const plan = usePlanSelectionStore(planSelectionStoreSelector.plan);
  const isYearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);
  const promoCode = usePlanSelectionStore(planSelectionStoreSelector.promoCode);

  const freeTrial = useFreeTrial();
  const planChangeState = useSubscriptionChangeState({ currentSubscription });
  const price = usePlanPrice({ plan, isYearly });

  if (!currentSubscription) {
    return null;
  }

  let amount = 0;
  let originalAmount = undefined;
  const currency = price.currency;

  // handles the case where there is no current subscription plan
  if (currentSubscription.pastDue && !changeSubscriptionPreview) {
    amount = currentSubscription.totalAmount;

    // when the user is changing from an existing plan
  } else if (planChangeState && changeSubscriptionPreview) {
    amount = changeSubscriptionPreview.nextInvoiceAmount;
    if (promoCode) {
      const nextInvoiceAmount = changeSubscriptionPreview.nextInvoiceAmount;
      // Discount is based on the PLAN amount, not the prorated amount
      const discount =
        calculateDiscount(
          { amount: changeSubscriptionPreview.plan.amount, currency },
          promoCode
        ) ?? 0;
      amount = nextInvoiceAmount - discount;
      originalAmount = nextInvoiceAmount;
    }
  } else if (planChangeState || !currentSubscription?.canceledAt) {
    amount = currentSubscription.nextInvoiceAmount ?? 0;
  }

  const { date, label } = getNextChargeDateAndLabel(
    currentSubscription,
    changeSubscriptionPreview,
    freeTrial,
    planChangeState
  );

  return {
    value: {
      amount,
      currency,
      originalAmount: originalAmount ?? amount,
    },
    label,
    date,
    note: freeTrial.isAvailable ? FREE_TRIAL_PAYMENT_NOTE : undefined,
    seats: getSeatsData(changeSubscriptionPreview, currency),
  };
};

export default useNextCharge;
